import { FC, useCallback, useState } from 'react'
import { ExamBoards, IBookedCourse, ICourseDetails } from '@/types/firebase'
import { useRouter } from 'next/navigation'
import { CourseOverviewTabKey, IGenericError } from '@/types/shared'
import { CommonCoursePreviewCard } from '@/components/ui'
import { SelectCourseSwitcher } from './SelectCourseSwitcher'
import {
  createBookedCourse,
  updateBookedCourse,
} from '@/firebase/booked-courses'
import { notify } from '@/utils/index'
import { Timestamp } from 'firebase/firestore'

interface Props {
  studentId: string
  courseWithDetails: ICourseDetails
  handleCourseSelect: (
    courseWithDetails: ICourseDetails,
    selectedExamBoard: ExamBoards,
  ) => Promise<void>
  handleCourseDeSelect: (courseWithDetails: ICourseDetails) => Promise<void>
  isLoading: boolean
  isSelected: boolean
  handleModalClose: () => void
  tabToRedirect?: CourseOverviewTabKey
}

export const SelectCoursesModalCard: FC<Props> = ({
  studentId,
  courseWithDetails,
  handleCourseSelect,
  handleCourseDeSelect,
  isLoading,
  isSelected,
  handleModalClose,
  tabToRedirect = CourseOverviewTabKey.Overview,
}) => {
  const [isSelectExamBoardModalOpened, setIsSelectExamBoardModalOpened] =
    useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [
    shouldRedirectAfterExamBoardSelected,
    setShouldRedirectAfterExamBoardSelected,
  ] = useState(false)

  const router = useRouter()

  const handleRedirectToCourse = useCallback(
    async (
      courseDetails: ICourseDetails,
      userId: string,
      examBoard?: ExamBoards,
    ) => {
      if (!userId) {
        return
      }

      try {
        setIsRedirecting(true)

        let bookedCourse: IBookedCourse | undefined = courseDetails.bookedCourse
        let isNewBookedCourse = false

        if (examBoard) {
          // book course if no one found
          if (!bookedCourse.id) {
            bookedCourse = await createBookedCourse({
              userId,
              course: courseDetails.course,
              partialBookedCourse: {
                isSelected: true, // set bookedCourse as selected by default on course overview page redirect
                selectionDate: Timestamp.now(),
                lastInteractionDate: Timestamp.now(),
                examBoard,
              },
            })
            isNewBookedCourse = !!bookedCourse.id
          } else {
            // update booked course examBoard field
            await updateBookedCourse(bookedCourse.id, {
              examBoard,
              isSelected: true, // set bookedCourse as selected by default on course overview page redirect
              selectionDate: Timestamp.now(),
              lastInteractionDate: Timestamp.now(),
            })

            bookedCourse = { ...bookedCourse, examBoard, isSelected: true }
          }
        }

        const path = `/student/${courseWithDetails.course.id}`

        const params = new URLSearchParams()

        if (bookedCourse.id) {
          params.append('bookedId', bookedCourse.id)
        }

        params.append('tab', tabToRedirect)

        // jump to the first lesson if student is in onboarding flow and course is just selected
        if (courseDetails.firstLessonId) {
          const firstTheme = courseDetails?.schemaDto?.[0]
          const firstModule = firstTheme?.children?.[0]
          const firstTopic = firstModule?.children?.[0]

          if (
            bookedCourse.id &&
            isNewBookedCourse &&
            firstTheme?.id &&
            firstModule?.id &&
            firstTopic?.id &&
            courseDetails.firstLessonId
          ) {
            params.set('tab', CourseOverviewTabKey.Lesson)
            params.set('lessonId', courseDetails.firstLessonId)
            params.set('themeId', firstTheme.id)
            params.set('moduleId', firstModule.id)
            params.set('topicId', firstTopic.id)
          }
        }

        const queryString = params.toString()

        const href = `${path}?${queryString}`

        router.push(href)

        handleModalClose()
        setShouldRedirectAfterExamBoardSelected(false)
      } catch (error: unknown) {
        console.error(
          'error in handleCourseSelect :>> ',
          (error as IGenericError)?.message || error,
        )

        if (error && typeof error === 'object' && 'message' in error) {
          notify('error', error.message)
        }
      } finally {
        setIsRedirecting(false)
      }
    },
    [courseWithDetails.course.id, handleModalClose, router, tabToRedirect],
  )

  return (
    <li className={`basis-1/2 rounded-v8`}>
      <CommonCoursePreviewCard
        course={courseWithDetails.course}
        isLoading={isLoading || isRedirecting}
        isSelected={isSelected}
        variant="minimal"
        examBoard={
          courseWithDetails.bookedCourse?.isSelected &&
          courseWithDetails.bookedCourse.examBoard
            ? courseWithDetails.bookedCourse.examBoard
            : undefined
        }
        wrapperClassName={`block w-full h-full rounded-v8 overflow-hidden bg-white border border-v8-line shadow-v8-card transition-all duration-200 ${
          isSelected ? 'ring-2 ring-v8-blue' : ''
        }`}
        contentClassName={
          'relative z-[0] w-full h-full min-h-[168px] flex justify-between flex-col gap-4 rounded-v8 bg-white transition duration-400 overflow-hidden'
        }
        handleCardClick={async () => {
          // redirect to already booked course
          if (
            courseWithDetails.bookedCourse?.isSelected &&
            courseWithDetails.bookedCourse.examBoard
          ) {
            await handleRedirectToCourse(courseWithDetails, studentId)
            return
          }

          // if course contains only 1 exam board use it by default to prevent SelectExamBoardChange modal appear
          if (courseWithDetails.course.examBoards.length === 1) {
            await handleRedirectToCourse(
              courseWithDetails,
              studentId,
              courseWithDetails.course.examBoards[0],
            )
            return
          }

          setShouldRedirectAfterExamBoardSelected(true)
          setIsSelectExamBoardModalOpened(true)
        }}
        additionalContent={
          <>
            {/* Course-select Switcher */}
            <div className="absolute top-4 right-4 z-[20]">
              <SelectCourseSwitcher
                handleSwitcherChange={async (v) => {
                  if (!v) {
                    handleCourseDeSelect(courseWithDetails)
                    return
                  }

                  if (!courseWithDetails.course.examBoards.length) {
                    console.error(
                      'Unexpected behavior. Exam boards array is empty for course:',
                      courseWithDetails.course,
                    )

                    return
                  }

                  // book course if course contains only one exam board
                  if (courseWithDetails.course.examBoards.length === 1) {
                    await handleCourseSelect(
                      courseWithDetails,
                      courseWithDetails.course.examBoards[0],
                    )

                    return
                  }

                  // open select course modal if course contains more than one exam boards
                  setIsSelectExamBoardModalOpened(true)
                }}
                wrapperClassName=""
                popover={
                  <div
                    className={`absolute z-[5] bottom-[-28px] right-0 px-3 py-1.5 bg-v8-ink text-white rounded-md shadow-lg`}
                  >
                    <div
                      className={`before:absolute before:content-[''] before:w-0 before:h-0 before:border-b-[6px] before:border-l-[6px] before:border-r-[6px] before:border-b-gray-900 before:border-l-transparent before:border-r-transparent before:right-[10px] before:top-[-6px]`}
                    ></div>
                    <p className={`text-[10px] font-medium text-nowrap`}>
                      {isSelected ? `Deselect` : `Select`}
                    </p>
                  </div>
                }
                isLoading={isLoading}
                isChecked={isSelected}
                selectExamBoardModalProps={{
                  id: `select-exam-board-modal-${courseWithDetails.course.id}`,
                  course: courseWithDetails.course,
                  handleConfirmClick: async (examBoard) => {
                    if (shouldRedirectAfterExamBoardSelected) {
                      await handleRedirectToCourse(
                        courseWithDetails,
                        studentId,
                        examBoard,
                      )
                    } else {
                      await handleCourseSelect(courseWithDetails, examBoard)
                    }
                    setIsSelectExamBoardModalOpened(false)
                  },
                  isSelectExamBoardModalOpened,
                  isLoading,
                  onClose: () => {
                    setIsSelectExamBoardModalOpened(false)
                  },
                  customZIndexClass: 'z-[665]',
                }}
              />
            </div>
          </>
        }
      />
    </li>
  )
}
